import { api } from './api';

export type SteeringKind = 'control' | 'correction' | 'policy' | 'material' | 'request' | 'other';
export type ControlValue = 'start' | 'break' | 'resume' | 'wrap' | 'drain-now' | 'phase';
export type SteeringSource = 'web' | 'hud' | 'ring' | 'claude-code' | 'mcp' | 'system';
export type SteeringStatus = 'active' | 'withdrawn' | 'done';
export type DrainControlState = 'pre-start' | 'live' | 'break' | 'wrap';

export interface SteeringNote {
  id: string;
  sessionId: string;
  kind: SteeringKind;
  text: string;
  value?: ControlValue;
  until?: string;
  phase?: string;
  source: SteeringSource;
  by: string | null;
  seq: number;
  status: SteeringStatus;
  consumedAt?: string;
  target?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SteerInput {
  text?: string;
  kind?: SteeringKind;
  value?: ControlValue;
  until?: string;
  phase?: string;
  source?: 'web' | 'hud';
}

export interface SteerResult {
  note: SteeringNote;
  warnings?: string[];
}

/** The fields of GET /drain that the steering and control UI reads. */
export interface DrainControl {
  enabled: boolean;
  control: DrainControlState | null;
  breakUntil: string | null;
  epoch: number;
  forceNext: boolean;
  lastDrainAt: string | null;
  nextDrainAt: string | null;
}

export const steeringService = {
  /** Notes come back oldest first. */
  async list(sessionId: string, status: 'active' | 'all' = 'active'): Promise<SteeringNote[]> {
    const res = await api.get(`/api/sessions/${sessionId}/steering`, { params: { status } });
    return res.data?.notes ?? [];
  },

  async steer(sessionId: string, input: SteerInput): Promise<SteerResult> {
    const res = await api.post(`/api/sessions/${sessionId}/steering`, input);
    return res.data;
  },

  async update(sessionId: string, noteId: string, status: 'withdrawn' | 'done'): Promise<SteeringNote> {
    const res = await api.patch(`/api/sessions/${sessionId}/steering/${encodeURIComponent(noteId)}`, { status });
    return res.data;
  },

  async enableDrain(sessionId: string): Promise<void> {
    await api.put(`/api/sessions/${sessionId}/drain`, { enabled: true });
  },

  async getDrain(sessionId: string): Promise<DrainControl> {
    const res = await api.get(`/api/sessions/${sessionId}/drain`);
    return res.data;
  },
};

export const steerError = (e: any, fallback: string): string => e?.response?.data?.error || fallback;
